/** biome-ignore-all lint/correctness/useExhaustiveDependencies: <explanation> */

import { useEffect, useMemo, useState } from 'react'
import { Controller, useWatch } from 'react-hook-form'
import { Panel } from '@/components/panel'
import { PanelBody } from '@/components/panel/components/panel-body'
import { PanelHeader } from '@/components/panel/components/panel-header'
import { PanelSeparator } from '@/components/panel/components/panel-separator'
import { PanelSubtitle } from '@/components/panel/components/panel-subtitle'
import { PanelTitle } from '@/components/panel/components/panel-title'
import { REQUEST_STATUS } from '@/constants/request-status'
import type { DesiredEducationCourse } from '@/types/education/desired-education-course'
import type { StudentRequestedCoursesFormMethods } from '@/types/forms/student-requested-courses-form-methods'
import { SelectSerie } from './components/select-serie'
import { TranscriptMetrics } from './components/transcript-metrics'
import { TranscriptTable } from './components/transcript-table'
import { useCourseMetrics } from './hooks/use-course-metrics'
import { useTranscriptSerie } from './hooks/use-transcript-serie'

interface SimulatedTranscriptProps {
  subTitle?: string
  courses?: DesiredEducationCourse[]
  formMethods?: StudentRequestedCoursesFormMethods
  updateTranscript?: boolean
  onlyView?: boolean
  requestStatus?: string
}

export const SimulatedTranscript = ({
  subTitle,
  courses,
  formMethods,
  onlyView,
  requestStatus
}: SimulatedTranscriptProps) => {
  const [canDetermineSerie, setCanDetermineSerie] = useState(true)

  const {
    actions: { determineStudentSerie, createSerieOptions }
  } = useTranscriptSerie()

  const {
    actions: { calculateMetrics }
  } = useCourseMetrics()

  const [courseEquivalences, serie] = useWatch({
    control: formMethods?.control,
    name: ['courseEquivalences', 'serie']
  })

  const serieOptions = useMemo(() => {
    return createSerieOptions(courses)
  }, [courses])

  const metrics = useMemo(() => {
    return calculateMetrics(courseEquivalences, courses || [])
  }, [courseEquivalences, courses])

  const sortedCourses = useMemo(() => {
    return courses
      ? [...courses].sort((a, b) => Number(a.serie) - Number(b.serie))
      : []
  }, [courses])

  function determineSerie() {
    if (requestStatus === REQUEST_STATUS.UNDER_REVIEW.id && canDetermineSerie) {
      const bestSerie = determineStudentSerie(
        courseEquivalences,
        sortedCourses ?? []
      )

      if (bestSerie && bestSerie !== serie) {
        formMethods?.setValue?.('serie', bestSerie)
      }
    }
  }

  useEffect(() => {
    if (!canDetermineSerie) return
    determineSerie()
  }, [courseEquivalences])

  return (
    <Panel>
      <PanelHeader className="flex items-center justify-between">
        <div>
          <PanelTitle>Novo Curso - Grade Curricular</PanelTitle>
          <PanelSubtitle>{subTitle}</PanelSubtitle>
        </div>
        <div className="w-60">
          <Controller
            control={formMethods?.control}
            name="serie"
            render={({ field }) => (
              <SelectSerie
                options={serieOptions}
                disabledSelect={
                  onlyView || requestStatus !== REQUEST_STATUS.UNDER_REVIEW.id
                }
                field={{
                  value: field.value,
                  onChange: (value: string) => {
                    if (canDetermineSerie) setCanDetermineSerie(false)
                    field.onChange(value)
                  }
                }}
              />
            )}
          />
        </div>
      </PanelHeader>
      <PanelBody>
        <div className="px-6 py-4">
          <TranscriptMetrics
            coursesUsed={metrics.coursesUsed}
            totalCourses={metrics.totalCourses}
            totalWorkload={metrics.totalWorkload}
            workloadUsed={metrics.workloadUsed}
          />
        </div>

        <PanelSeparator />

        <div className="px-6 py-6 flex flex-col">
          <TranscriptTable
            desiredCourses={sortedCourses}
            courseEquivalences={courseEquivalences}
            selectedSerie={serie}
            onlyView={onlyView}
          />
        </div>
      </PanelBody>
    </Panel>
  )
}
